import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import { supabase } from '../supabase/config';
import SubjectHeader from './SubjectHeader';
import { useTheme } from '../hooks/useTheme';
import SectionErrorBoundary from './SectionErrorBoundary';
import { storage, STORAGE_KEYS } from '../utils/storage';

// Import child components - ensure they're imported before use
import InvitationManager from './InvitationManager';
import ChildProgressView from './ChildProgressView';
import ActivityTimeline from './ActivityTimeline';
import ParentControls from './ParentControls';
import GoalSetting from './GoalSetting';
import ChildSettingsManager from './ChildSettingsManager';
import TimeManagement from './TimeManagement';
import { LazyProfileManager } from './LazyComponent';

const ParentDashboard = () => {
  const { user, userProfile, getChildren, logout } = useUser();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileManager, setShowProfileManager] = useState(false);
  const [isSupabaseDisabled, setIsSupabaseDisabled] = useState(() => {
    const storageFlag = storage.get(STORAGE_KEYS.SUPABASE_DISABLED);
    const disabled = storageFlag === true || storageFlag === 'true' || localStorage.getItem('supabaseDisabled') === 'true';
    return !!disabled;
  });
  
  // Use ref to store loadChildren function to avoid closure issues in subscriptions
  const loadChildrenRef = useRef(null);
  const loadChildrenDebounceRef = useRef(null);
  
  // Use a parent-themed gradient to match app styling
  const parentThemeColors = 'from-purple-400 via-pink-500 to-indigo-600';
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserMenu) {
        const menuElement = document.querySelector('.parent-dashboard-user-menu');
        if (menuElement && !menuElement.contains(e.target)) {
          setShowUserMenu(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Log when component mounts/updates - simplified to avoid initialization issues
  useEffect(() => {
    if (user || userProfile) {
      logger.info('[ParentDashboard] Component rendered, user:', {
        hasUser: !!user,
        userId: user?.id,
        hasUserProfile: !!userProfile,
        email: userProfile?.email,
        profileId: userProfile?.id
      });
    }
  }, [user?.id, userProfile?.id]);

  const refreshSyncStatus = useCallback(() => {
    const storageFlag = storage.get(STORAGE_KEYS.SUPABASE_DISABLED);
    const disabled = storageFlag === true || storageFlag === 'true' || localStorage.getItem('supabaseDisabled') === 'true';
    setIsSupabaseDisabled(disabled);
  }, []);

  const loadChildren = useCallback(async () => {
    if (!userProfile?.email) {
      logger.warn('[ParentDashboard] No userProfile.email, skipping loadChildren');
      setIsLoading(false);
      return;
    }
    logger.info('[ParentDashboard] Loading children for:', userProfile.email);
    setIsLoading(true);
    try {
      const childrenData = await getChildren(userProfile.email);
      logger.info('[ParentDashboard] Children loaded:', childrenData?.length || 0);
      setChildren(childrenData || []);
      // Set first child as selected if we have children and no selection yet
      setSelectedChild(prev => {
        if (!prev && childrenData && childrenData.length > 0) {
          return childrenData[0];
        }
        return prev;
      });
    } catch (error) {
      logger.error('[ParentDashboard] Error loading children:', error);
      setChildren([]);
    } finally {
      setIsLoading(false);
      logger.info('[ParentDashboard] loadChildren complete, isLoading set to false');
      refreshSyncStatus();
    }
  }, [userProfile?.email, getChildren, refreshSyncStatus]);
  
  // Store loadChildren in ref for use in subscriptions
  useEffect(() => {
    loadChildrenRef.current = loadChildren;
  }, [loadChildren]);

  const handleRetrySync = useCallback(() => {
    storage.remove(STORAGE_KEYS.SUPABASE_DISABLED);
    localStorage.removeItem('supabaseDisabled');
    setIsSupabaseDisabled(false);
    if (loadChildrenRef.current) {
      loadChildrenRef.current();
    }
  }, []);

  const scheduleLoadChildren = useCallback(() => {
    if (loadChildrenDebounceRef.current) {
      clearTimeout(loadChildrenDebounceRef.current);
    }
    loadChildrenDebounceRef.current = setTimeout(() => {
      if (loadChildrenRef.current) {
        loadChildrenRef.current();
      }
    }, 250);
  }, []);

  useEffect(() => {
    logger.info('[ParentDashboard] useEffect triggered, userProfile:', {
      hasEmail: !!userProfile?.email,
      email: userProfile?.email,
      hasUserProfile: !!userProfile
    });
    
    if (userProfile?.email) {
      loadChildren();
    } else {
      logger.warn('[ParentDashboard] No userProfile.email, setting isLoading to false');
      setIsLoading(false);
    }
    refreshSyncStatus();
  }, [userProfile?.email, loadChildren, refreshSyncStatus]);

  // Separate timeout effect to prevent infinite loops
  useEffect(() => {
    // Safety timeout - if loading takes too long, stop loading
    const loadingTimeout = setTimeout(() => {
      setIsLoading(prev => {
        if (prev) {
          logger.warn('[ParentDashboard] Loading timeout reached, stopping loading state');
          return false;
        }
        return prev;
      });
    }, 10000); // 10 second timeout
    
    return () => clearTimeout(loadingTimeout);
  }, []); // Only run once on mount

  // Set up real-time subscriptions for child progress updates
  useEffect(() => {
    if (!userProfile?.id || children.length === 0) return;

    const childIds = children.map(child => child.id);
    if (childIds.length === 0) return;

    const childIdsFilter = childIds.join(',');
    
    // Subscribe to user profile updates for all children via single channel
    const progressChannel = supabase
      .channel('child-progress-batch')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=in.(${childIdsFilter})`
        },
        () => {
          scheduleLoadChildren();
        }
      )
      .subscribe();

    // Subscribe to activity log updates
    const activityChannel = supabase
      .channel('child-activities')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'child_activity_log',
          filter: `child_id=in.(${childIdsFilter})`
        },
        () => {
          scheduleLoadChildren();
        }
      )
      .subscribe();

    return () => {
      clearTimeout(loadChildrenDebounceRef.current);
      // Cleanup subscriptions
      supabase.removeChannel(progressChannel);
      supabase.removeChannel(activityChannel);
    };
  }, [userProfile?.id, children, scheduleLoadChildren]);

  // Calculate total progress - simplified to avoid useMemo initialization issues
  const getTotalProgress = () => {
    if (!children.length) return { totalScore: 0, totalLessons: 0, totalStreak: 0 };
    
    return children.reduce((totals, child) => ({
      totalScore: totals.totalScore + (child.progress?.score || 0),
      totalLessons: totals.totalLessons + (child.progress?.completed_lessons?.length || 0),
      totalStreak: Math.max(totals.totalStreak, child.progress?.learning_streak || 0)
    }), { totalScore: 0, totalLessons: 0, totalStreak: 0 });
  };

  // Calculate recent activity - simplified to avoid useMemo initialization issues
  const getRecentActivity = () => {
    const allActivity = children.flatMap(child => 
      (child.progress?.completed_lessons || []).map(lesson => ({
        childName: child.display_name,
        lesson,
        timestamp: child.progress?.last_active || new Date()
      }))
    );
    
    return allActivity
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);
  };

  const latestSyncedAt = useMemo(() => {
    const timestamps = children
      .map(child => child.updated_at || child.progress?.last_active)
      .filter(Boolean)
      .map(ts => new Date(ts).getTime())
      .filter(time => !Number.isNaN(time));
    if (!timestamps.length) return null;
    return new Date(Math.max(...timestamps));
  }, [children]);

  const formatRelativeTime = (date) => {
    if (!date) return 'Not yet synced';
    const now = Date.now();
    const diffMs = now - date.getTime();
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getChildSyncStatus = (child) => {
    const ts = child.updated_at || child.progress?.last_active || latestSyncedAt;
    const dateObj = ts ? new Date(ts) : null;
    return {
      label: isSupabaseDisabled
        ? 'Local only (sync paused)'
        : `Last synced: ${formatRelativeTime(dateObj)}`,
      title: dateObj ? dateObj.toISOString() : 'No sync recorded'
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading your children's progress...</div>
        </div>
      </div>
    );
  }

  const totalProgress = getTotalProgress();
  // Get display name with fallbacks - use user.email if userProfile not loaded yet
  const displayName = userProfile?.display_name 
    || userProfile?.displayName 
    || userProfile?.email?.split('@')[0]
    || user?.email?.split('@')[0]
    || user?.user_metadata?.full_name
    || user?.user_metadata?.name
    || user?.user_metadata?.display_name
    || 'User';

  return (
    <div className={`min-h-screen bg-gradient-to-br ${parentThemeColors}`}>
      <SubjectHeader
        icon="👨‍👩‍👧‍👦"
        title="Parent Dashboard"
        subtitle={
          children.length > 0 
            ? `${children.length} ${children.length === 1 ? 'child' : 'children'} | ${totalProgress.totalScore} pts | ${totalProgress.totalLessons} lessons`
            : `Welcome, ${displayName}`
        }
        mobileSubtitle={
          children.length > 0
            ? `${children.length} ${children.length === 1 ? 'child' : 'children'}`
            : displayName
        }
        accent={parentThemeColors}
        compactOnScroll
        onBack={true}
        rightContent={
          <div className="flex items-center gap-2">
            {/* User Menu (Hamburger) */}
            <div className="relative parent-dashboard-user-menu">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowUserMenu(!showUserMenu);
                }}
                className="bg-purple-900/95 hover:bg-purple-800/95 active:bg-purple-700/95 text-white rounded-full px-4 py-3 font-semibold shadow-xl hover:shadow-2xl transition-all border-2 border-white/30"
                title="Menu"
                type="button"
                style={{ 
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {showUserMenu && (
                <div 
                  className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    {userProfile && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowProfileManager(true);
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 cursor-pointer transition-colors"
                      >
                        <p className="text-xs text-gray-500 mb-1">Logged in as</p>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">👤</span>
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {userProfile.display_name || userProfile.email}
                          </p>
                        </div>
                        <p className="text-xs text-purple-600 mt-1">Click to view profile</p>
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.hash = '/';
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span>🏠</span>
                      <span>Home</span>
                    </button>
                    {userProfile && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowProfileManager(true);
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-200"
                      >
                        <span>⚙️</span>
                        <span>Settings</span>
                      </button>
                    )}
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to log out?')) {
                          await logout();
                          setShowUserMenu(false);
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-gray-200"
                    >
                      <span>🚪</span>
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      />

      <main id="main-content" className={`min-h-screen bg-gradient-to-br ${parentThemeColors} p-4 md:p-8`}>
        <div className="max-w-7xl mx-auto">
        <div className="mb-4 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className={`inline-flex h-3 w-3 rounded-full ${isSupabaseDisabled ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`} aria-hidden="true"></span>
            <div>
              <p className="font-semibold text-gray-900">
                {isSupabaseDisabled ? 'Sync paused (local only)' : 'Sync active'}
              </p>
              <p className="text-sm text-gray-600">
                {isSupabaseDisabled
                  ? 'Supabase sync disabled due to a policy/RLS issue. Data stays local.'
                  : `Last synced: ${formatRelativeTime(latestSyncedAt)}`}
              </p>
            </div>
          </div>
          {isSupabaseDisabled && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleRetrySync}
                className="inline-flex items-center justify-center px-4 py-2 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Retry sync
              </button>
            </div>
          )}
        </div>
        {children.length === 0 ? (
          <>
            <div className="text-center py-12 mb-8">
              <div className="text-6xl mb-4">👶</div>
              <h2 className="text-2xl font-bold text-white mb-4 drop-shadow-lg">No Children Found</h2>
              <p className="text-white/90 mb-6 drop-shadow-md">
                Invite your child to connect their account to your dashboard.
              </p>
            </div>
            <InvitationManager onInvitationCreated={loadChildren} />
          </>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
                <div className="flex items-center">
                  <div className="text-3xl mr-4">👶</div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{children.length}</div>
                    <div className="text-sm text-gray-600">Children</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
                <div className="flex items-center">
                  <div className="text-3xl mr-4">⭐</div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{getTotalProgress().totalScore}</div>
                    <div className="text-sm text-gray-600">Total Points</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
                <div className="flex items-center">
                  <div className="text-3xl mr-4">📚</div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{getTotalProgress().totalLessons}</div>
                    <div className="text-sm text-gray-600">Lessons Completed</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
                <div className="flex items-center">
                  <div className="text-3xl mr-4">🔥</div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{getTotalProgress().totalStreak}</div>
                    <div className="text-sm text-gray-600">Best Streak</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Invitation Manager */}
            <InvitationManager onInvitationCreated={loadChildren} />

            {/* Children List */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg mb-8 mt-8 border border-white/20">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Your Children 👨‍👩‍👧‍👦</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {children.map(child => (
                    <div
                      key={child.id}
                      onClick={() => setSelectedChild(child)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                        selectedChild?.id === child.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <div className="text-2xl mr-3">
                          {child.avatar === 'girl' ? '👧' : 
                           child.avatar === 'boy' ? '👦' : 
                           child.avatar === 'princess' ? '👸' : 
                           child.avatar === 'superhero' ? '🦸' : 
                           child.avatar === 'robot' ? '🤖' : 
                           child.avatar === 'unicorn' ? '🦄' : 
                           child.avatar === 'dinosaur' ? '🦕' : '👤'}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{child.display_name}</div>
                          <div className="text-sm text-gray-600">{child.email}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-center">
                          <div className="font-semibold text-purple-600">{child.progress?.score || 0}</div>
                          <div className="text-gray-500">Points</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-green-600">{child.progress?.learning_streak || 0}</div>
                          <div className="text-gray-500">Streak</div>
                        </div>
                      </div>
                      {(() => {
                        const childSync = getChildSyncStatus(child);
                        return (
                          <div className="mt-3 text-xs text-gray-600 flex items-center gap-2" title={childSync.title}>
                            <span
                              className={`inline-flex h-2.5 w-2.5 rounded-full ${isSupabaseDisabled ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}
                              aria-hidden="true"
                            ></span>
                            <span>{childSync.label}</span>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg mb-8 border border-white/20">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px overflow-x-auto">
                  {['overview', 'progress', 'analytics', 'goals', 'settings', 'time'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab
                          ? 'border-purple-500 text-purple-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab === 'overview' && '📊 Overview'}
                      {tab === 'progress' && '📈 Progress'}
                      {tab === 'analytics' && '📉 Analytics'}
                      {tab === 'goals' && '🎯 Goals'}
                      {tab === 'settings' && '⚙️ Settings'}
                      {tab === 'time' && '⏱️ Time'}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && selectedChild && (
                  <SectionErrorBoundary 
                    name="OverviewTab"
                    title="Overview Unavailable"
                    message="The overview section couldn't load. Try refreshing the page."
                  >
                    <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-purple-600 mb-2">
                          {selectedChild.progress?.score || 0}
                        </div>
                        <div className="text-gray-600">Total Points</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          {selectedChild.progress?.learning_streak || 0}
                        </div>
                        <div className="text-gray-600">Learning Streak</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600 mb-2">
                          {selectedChild.progress?.completed_lessons?.length || 0}
                        </div>
                        <div className="text-gray-600">Lessons Completed</div>
                      </div>
                    </div>

                    {/* Achievements */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievements 🏆</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {selectedChild.progress?.achievements?.map(achievement => (
                          <div key={achievement.id} className="text-center p-3 bg-yellow-50 rounded-lg">
                            <div className="text-2xl mb-1">{achievement.emoji}</div>
                            <div className="text-sm font-semibold text-yellow-800">{achievement.name}</div>
                          </div>
                        )) || (
                          <div className="col-span-full text-center text-gray-500 py-4">
                            No achievements yet. Keep learning! 🌟
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Preferences */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Preferences ⚙️</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-semibold text-gray-700">Difficulty</div>
                          <div className="text-lg text-purple-600 capitalize">
                            {selectedChild.preferences?.difficulty || 'medium'}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-semibold text-gray-700">Sound</div>
                          <div className="text-lg text-green-600">
                            {selectedChild.preferences?.sound_enabled ? '🔊' : '🔇'}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-semibold text-gray-700">Music</div>
                          <div className="text-lg text-blue-600">
                            {selectedChild.preferences?.music_enabled ? '🎵' : '🔇'}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-semibold text-gray-700">Theme</div>
                          <div className="text-lg text-orange-600 capitalize">
                            {selectedChild.preferences?.theme || 'light'}
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </SectionErrorBoundary>
                )}

                {activeTab === 'progress' && selectedChild && (
                  <SectionErrorBoundary 
                    name="ProgressTab"
                    title="Progress View Unavailable"
                    message="The progress view couldn't load right now."
                  >
                    <ChildProgressView child={selectedChild} />
                  </SectionErrorBoundary>
                )}

                {activeTab === 'analytics' && selectedChild && (
                  <SectionErrorBoundary 
                    name="AnalyticsTab"
                    title="Analytics Unavailable"
                    message="The activity timeline couldn't load right now."
                  >
                    <ActivityTimeline childId={selectedChild.id} dateRange="all" />
                  </SectionErrorBoundary>
                )}

                {activeTab === 'goals' && selectedChild && (
                  <SectionErrorBoundary 
                    name="GoalsTab"
                    title="Goals Section Unavailable"
                    message="The goals section couldn't load right now."
                  >
                    <GoalSetting 
                    child={selectedChild} 
                    onGoalCreated={() => {
                      // Refresh child data if needed
                      loadChildren();
                    }}
                  />
                  </SectionErrorBoundary>
                )}

                {activeTab === 'settings' && selectedChild && (
                  <SectionErrorBoundary 
                    name="SettingsTab"
                    title="Settings Unavailable"
                    message="The settings section couldn't load right now."
                  >
                    <div className="space-y-6">
                    <ParentControls 
                      child={selectedChild}
                      onUpdate={(updatedChild) => {
                        setSelectedChild(updatedChild);
                        loadChildren();
                      }}
                    />
                    <ChildSettingsManager 
                      child={selectedChild}
                      onUpdate={(updatedChild) => {
                        setSelectedChild(updatedChild);
                        loadChildren();
                      }}
                    />
                    </div>
                  </SectionErrorBoundary>
                )}

                {activeTab === 'time' && selectedChild && (
                  <SectionErrorBoundary 
                    name="TimeTab"
                    title="Time Management Unavailable"
                    message="The time management section couldn't load right now."
                  >
                    <TimeManagement child={selectedChild} />
                  </SectionErrorBoundary>
                )}

                {!selectedChild && (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">👆</div>
                    <p className="text-gray-600">Select a child to view their details</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg mt-8 border border-white/20">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Recent Activity 📈</h2>
              </div>
              <div className="p-6">
                {getRecentActivity().length > 0 ? (
                  <div className="space-y-3">
                    {getRecentActivity().map((activity, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center">
                          <div className="text-green-500 mr-3">✓</div>
                          <div>
                            <div className="font-medium text-gray-900">{activity.childName}</div>
                            <div className="text-sm text-gray-600">Completed: {activity.lesson}</div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <div className="text-4xl mb-2">📚</div>
                    <div>No recent activity. Encourage your children to start learning!</div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        </div>
      </main>
      
      {/* Profile Manager Modal */}
      {showProfileManager && (
        <LazyProfileManager
          onClose={() => setShowProfileManager(false)}
        />
      )}
    </div>
  );
};

ParentDashboard.displayName = 'ParentDashboard';

// Export without memo to avoid bundling/initialization issues
export default ParentDashboard;
